import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // For navigation
import api from '../../services/api'; // Import the API service

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add'; // Icon for add button
function JournalManager({ token }) { // Assume token is passed as a prop
  const [entries, setEntries] = useState([]);
  const [goals, setGoals] = useState([]); // State to store user's goals
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate(); // Hook for navigation

  const fetchEntries = useCallback(async () => {
    if (!token) {
      setError('Authentication token is missing.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Fetch both entries and goals
      const [fetchedEntries, fetchedGoals] = await Promise.all([
        api.getJournalEntries(token),
        api.getGoals(token) // Fetch goals to look up descriptions
      ]);
      setEntries(fetchedEntries || []); // Ensure it's an array
      setGoals(fetchedGoals || []); // Store goals
    } catch (err) {
      console.error("Error fetching journal data:", err);
      setError(err.message || 'Failed to load journal entries.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAddNewEntry = () => {
    // Navigate to the editor route for a new entry
    // We'll define this route later
    navigate('/journal/new'); 
  };

  const handleViewEntry = (entryId) => {
    // Navigate to the editor route for viewing/editing an existing entry
    navigate(`/journal/entry/${entryId}`); 
  };

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            My Journal
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNewEntry}
          >
            New Entry
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {!loading && !error && (
          <List>
            {entries.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
                You haven't written any journal entries yet.
              </Typography>
            ) : (
              entries.map((entry) => (
                <ListItem key={entry._id} disablePadding>
                  <ListItemButton onClick={() => handleViewEntry(entry._id)}>
                    <ListItemText
                      primary={entry.title || `Entry - ${new Date(entry.date).toLocaleDateString()}`}
                      secondary={
                        <>
                          {`Written on: ${new Date(entry.date).toLocaleString()}`}
                          {entry.relatedGoal && goals.find(g => g._id === entry.relatedGoal) && (
                            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                              Linked to Goal: {goals.find(g => g._id === entry.relatedGoal)?.description.substring(0, 50)}{goals.find(g => g._id === entry.relatedGoal)?.description.length > 50 ? '...' : ''}
                            </Typography>
                          )}
                           {entry.behaviourTracked && (
                             <Typography variant="caption" display="block" sx={{ color: 'success.main', mt: 0.5 }}>
                               (Habit Tracked: {entry.completionStatus || 'N/A'})
                             </Typography>
                           )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        )}
      </Paper>

    </Box>
  );
}

export default JournalManager;
